import { useState } from "react";
import { useParams } from "react-router-dom";
import { Video as VideoIcon } from "lucide-react";
import { VideoPlayer } from "@/components/viewers/VideoPlayer";
import {
  GenericError,
  PaywallCard,
  ViewerScaffold,
} from "@/components/viewers/ViewerScaffold";
import { useViewerAccess } from "@/components/viewers/ChapterAccessGate";
import { ContentComingSoon } from "@/components/viewers/ContentComingSoon";
import { useSignedUrl, type Language } from "@/hooks/useSignedUrl";
import { useViewerTracker } from "@/hooks/useViewerTracker";
import { useMediaCompletion } from "@/hooks/useMediaCompletion";
import { cn } from "@/lib/cn";

const LANGUAGES: { id: Language; label: string; native: string }[] = [
  { id: "english", label: "English", native: "English" },
  { id: "hindi",   label: "Hindi",   native: "हिंदी" },
  { id: "kannada", label: "Kannada", native: "ಕನ್ನಡ" },
];

export function VideoPage() {
  const { chapterId } = useParams<{ chapterId: string }>();
  const { access, paywall, shouldFetch } = useViewerAccess(chapterId, "Video");
  // No dwell floor: "90% of the duration actually played" already carries the
  // time proof, and useMediaCompletion ignores seeks so scrubbing to the end
  // counts for nothing.
  const { reportEndReached } = useViewerTracker({ chapterId, mode: "video", active: paywall === null });
  const handlePlaybackProgress = useMediaCompletion({ onComplete: reportEndReached });

  const [language, setLanguage] = useState<Language>("english");

  const { url, isLoading, isError, isLocked, isNotFound, refresh } = useSignedUrl({
    chapterId,
    assetType: "video",
    language,
    enabled: shouldFetch,
  });

  const chapter = access.chapter;
  const classId = access.needsClassId ?? chapter?.class_id;

  const languageSwitcher = (
    <LanguageSwitcher
      value={language}
      onChange={(next) => {
        if (next === language) return;
        setLanguage(next);
      }}
      loadingLanguage={isLoading ? language : null}
    />
  );

  const missingTitle =
    language === "english"
      ? "The video for this chapter is almost ready"
      : `The ${LANGUAGES.find((l) => l.id === language)?.label} video for this chapter is almost ready`;

  return (
    <ViewerScaffold
      chapterTitle={chapter?.chapter_name ?? null}
      subjectName={null}
      modeLabel="Video Overview"
      modeIcon={VideoIcon}
      modeAccent="bg-red-500/10 text-red-600 dark:text-red-400"
    >
      {paywall ?? (isLocked ? (
        <PaywallCard modeLabel="Video" classId={classId} />
      ) : isError ? (
        <GenericError onRetry={refresh} />
      ) : (
        <div className="py-6 sm:py-8">
          {isNotFound ? (
            <>
              {languageSwitcher}
              <ContentComingSoon
                className="pt-6"
                chapterId={chapterId}
                chapterName={chapter?.chapter_name}
                title={missingTitle}
                modeIcon={VideoIcon}
                modeAccent="bg-red-500/10 text-red-600 dark:text-red-400"
              />
            </>
          ) : (
            <VideoPlayer
              key={`${chapterId}:${language}`}
              src={url ?? ""}
              progressKey={chapterId ? `${chapterId}:video:${language}` : undefined}
              onError={() => refresh()}
              onPlaybackProgress={handlePlaybackProgress}
              topSlot={languageSwitcher}
            />
          )}
        </div>
      ))}
    </ViewerScaffold>
  );
}

function LanguageSwitcher({
  value,
  onChange,
  loadingLanguage,
}: {
  value: Language;
  onChange: (lang: Language) => void;
  loadingLanguage: Language | null;
}) {
  return (
    <div
      role="tablist"
      aria-label="Video language"
      className="mx-auto flex max-w-xs items-center gap-1 rounded-lg border border-border bg-surface-2 p-1"
    >
      {LANGUAGES.map((l) => {
        const active = l.id === value;
        const loading = loadingLanguage === l.id;
        return (
          <button
            key={l.id}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(l.id)}
            className={cn(
              "flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
              active
                ? "bg-brand text-white shadow-sm"
                : "text-text-muted hover:text-text hover:bg-surface",
              loading && "opacity-80",
            )}
          >
            {l.native}
          </button>
        );
      })}
    </div>
  );
}
